import { Button } from '@trellis/roots/button'
import { StatusBadge } from '@trellis/roots/status'
import { AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

import { useExportJob } from '@/hooks/useReports'
import { downloadFile } from '@/lib/download-file'
import { formatBytes } from '@/lib/format'

const EXPORT_JOB_STATUS_STYLES = {
  PENDING: { pill: 'bg-amber-50 text-amber-800', dot: 'bg-amber-500' },
  RUNNING: { pill: 'bg-amber-50 text-amber-800', dot: 'bg-amber-500' },
  READY: { pill: 'bg-emerald-50 text-emerald-800', dot: 'bg-emerald-600' },
  FAILED: { pill: 'bg-red-50 text-red-800', dot: 'bg-red-600' },
}

const EXPORT_JOB_STATUS_LABELS = {
  PENDING: 'Preparing',
  RUNNING: 'Generating',
  READY: 'Ready',
  FAILED: 'Failed',
}

export interface ExportJobStatusProps {
  jobId: string
  /** Filename offered to the browser's save dialog once the archive is READY. */
  fileName?: string
}

/**
 * Polls the export job every 3s (see useExportJob) until it reaches a terminal status.
 * PENDING/RUNNING show a spinner, READY offers the download, FAILED surfaces the server's
 * errorMessage — the same job can be a standalone receipt bundle or an EOFY pack with
 * receipts included, so this component only knows about job status, not the source.
 */
export function ExportJobStatus({
  jobId,
  fileName = 'receipts.zip',
}: ExportJobStatusProps) {
  const { data: result, isLoading } = useExportJob(jobId)
  const job = result?.data

  function handleDownload(downloadUrl: string) {
    downloadFile(downloadUrl, fileName).catch((caughtError: unknown) => {
      const message =
        caughtError instanceof Error ? caughtError.message : 'Could not download the file'
      toast.error('Download failed', { description: message })
    })
  }

  if (isLoading && !job) {
    return (
      <div className="text-muted-foreground flex items-center gap-2 text-sm">
        <Loader2 className="size-4 animate-spin" />
        Checking status…
      </div>
    )
  }

  if (result?.error || !job) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-700">
        <AlertCircle className="size-4 shrink-0" />
        {result?.error?.message ?? 'Unable to check the export status.'}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <StatusBadge
          status={job.status}
          styles={EXPORT_JOB_STATUS_STYLES}
          labels={EXPORT_JOB_STATUS_LABELS}
        />
        {(job.status === 'PENDING' || job.status === 'RUNNING') && (
          <Loader2 className="text-muted-foreground size-4 animate-spin" />
        )}
      </div>

      {job.status === 'READY' && job.downloadUrl && (
        <div className="flex items-center justify-between gap-3">
          <p className="text-muted-foreground text-sm">
            {job.fileCount} file{job.fileCount === 1 ? '' : 's'} ·{' '}
            {formatBytes(job.totalSizeBytes)}
          </p>
          <Button size="sm" onClick={() => handleDownload(job.downloadUrl!)}>
            Download
          </Button>
        </div>
      )}

      {job.status === 'FAILED' && (
        <p className="flex items-start gap-2 text-sm text-red-700">
          <AlertCircle className="mt-0.5 size-4 shrink-0" />
          {job.errorMessage ?? 'The export failed. Try a narrower date range.'}
        </p>
      )}
    </div>
  )
}
